import { useContext, useState, useEffect} from 'react';

//context
import { ListContext } from '@/context/ListContext';
import { CombinedFeedContext } from '@/context/CombinedFeedContext';

//components
import { ISimpleTableRows } from 'thrive-components';

//api
import { getMainMenuId} from '@/api/lists';
import { addSourceListToCombinedFeed, getAllFeedModalItems, removeSourceListFromCombinedFeed } from '@/api/combinedFeed';

//hooks
import UseBasicBreadcrumbsForContentModals from  '@/components/hooks/contentModals/BasicBreadcrumbsForContentModals.hooks';

//interfaces
import { NotificationStatus } from  '@/interfaces/languages';
import { ISourceListItem, ISourceListsTable } from  '@/interfaces/CombinedFeed';
import { SourceListContentRow } from './SourceListContentRow';



const UseSourceListSelector = () => {
    const { appId, appGuid, listId, listIdRef} = useContext(ListContext)
    const {handleModalClose, setNotificationDetails, setModalIsLoading} = useContext(CombinedFeedContext);
    const { breadcrumbs, setBreadcrumb } = UseBasicBreadcrumbsForContentModals({ onBreadcrumbClick });

    const tableColumns = [{title: "Title", isToggleButton: false}, {title: "Audience", isToggleButton: false}, {title: "Actions", isToggleButton: false}]
    const showBackButton:boolean = breadcrumbs.length > 1;

    const [menuId, setMenuId] = useState<number>();
    const [isLoading, setIsLoading]=useState<boolean>(true);
    const [parentListId, setParentListId] = useState<number>();
    const [isRefreshingFeed, setIsRefreshingFeed]=useState<boolean>(false);
    const [moreContentAvailable, setMoreContentAvailable]=useState<boolean>(false);
    const [skip, setSkip] = useState<number>(0);
    const [contentData, setContentData] = useState<ISourceListItem[]>([]);
    const [alteringSourceListId, setAlteringSourceListId] = useState<number | undefined>(undefined);
    const [tableData, setTableData]=useState<ISourceListsTable>({
        simpleTableContent: { columns: tableColumns, rows: sourceListTableRows() },
        getMoreContent: getMoreContent,
        isLoading: isLoading,
        isInfiniteScroll: true,
        infiniteScrollFetchCondition: moreContentAvailable,
        noDataMessage: menuId ? "No content available" : "Something went wrong",
        hideActionsColumn: true
    });
    
    useEffect(()=> {
        getMenuId();
    }, []);

    useEffect(()=> {
        if (menuId) {
            setParentListId(menuId);
            setBreadcrumb(menuId, "Menu");
        }
    }, [menuId]);

    useEffect(()=> {
        if (!parentListId) { return }
        setIsLoading(true);
        getModalContent();
    }, [parentListId]);

    useEffect(()=> {
        if (alteringSourceListId && isRefreshingFeed) {
            getModalContent();
        }
    }, [isRefreshingFeed]);

    useEffect(() => {
        setTableData({
            simpleTableContent: { columns: tableColumns, rows: sourceListTableRows() },
            getMoreContent: getMoreContent,
            isInfiniteScroll: true,
            infiniteScrollFetchCondition: moreContentAvailable,
            isLoading: isLoading,
            noDataMessage: menuId ? "No lists found" : "Something went wrong",
            hideActionsColumn: true,
        });
    }, [contentData, isLoading, moreContentAvailable, parentListId, isRefreshingFeed, alteringSourceListId]);

    async function getModalContent(pagination?: boolean): Promise<void> {
        if (!pagination && !isRefreshingFeed) { setIsLoading(true);}
        if (!parentListId) { return }
        const top = isRefreshingFeed ? contentData.length : 20;

        getAllFeedModalItems(appId, parentListId?.toString(), top, pagination ? skip + top : 0, listId ?? listIdRef)
        .then((data) => {
            if (pagination) {
                setContentData((prevTableData) => [...(prevTableData || []), ...data?.records]);
            } else {
                setContentData(data?.records);
            }
            setSkip(data.skip);
            setMoreContentAvailable(data.moreAvailable);
        })
        .catch((error: any) => {
            const status = error?.response?.status;

            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message:
                    status === 500
                        ? "Something went wrong when fetching content"
                        : (error?.response?.data || "Something went wrong")
            });
        })
        .finally(() => {
            setIsLoading(false);
            setAlteringSourceListId(undefined);
            setIsRefreshingFeed(false);
        });
    }

    async function addSourceList(sourceListId: number){
        setModalIsLoading(true);
        setAlteringSourceListId(sourceListId);

        addSourceListToCombinedFeed(listIdRef || listId, sourceListId)
        .catch((error: any) => {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message: error.response?.data
            });
        })
        .finally(() =>   {
            setIsRefreshingFeed(true);
            setModalIsLoading(false);
        })
    }
    
    async function removeSourceList(sourceListId: number){
        setModalIsLoading(true);
        setAlteringSourceListId(sourceListId);
    
        removeSourceListFromCombinedFeed(listIdRef || listId, sourceListId)
        .catch((error: any) => {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message: error.response?.data
            });
        })
        .finally(() =>   {
            setIsRefreshingFeed(true);
            setModalIsLoading(false);
        })
    }

    async function getMenuId(): Promise<void> {
        getMainMenuId(appId)
        .then((data) => {
            setMenuId(data?.mainMenuId);
        })
    }

    function getMoreContent() {
        getModalContent(true);
    }

    function onOpenListClick(id: number, title: string) {
        setParentListId(id);
        setBreadcrumb(id, title);
    }

    function onBackButtonClick() {
        const prevBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
        if (prevBreadcrumb?.onClick) {
            prevBreadcrumb.onClick();
        }
        setParentListId(prevBreadcrumb.contentId);
    }

    function onBreadcrumbClick(id: number, title: string) {
        setIsLoading(true);
        setParentListId(id);
        setBreadcrumb(id, title);
    }

    function sourceListTableRows() {
        const isRefreshingModalFeed = (!!alteringSourceListId || isRefreshingFeed);
        
        return contentData
            .map((content) => SourceListContentRow(appGuid, content, onOpenListClick, addSourceList, removeSourceList, alteringSourceListId === Number(content.id), isRefreshingModalFeed))
            .filter((row): row is ISimpleTableRows => row !== undefined);
    }

    return {
        breadcrumbs,
        tableData,
        showBackButton,
        handleModalClose,
        onBackButtonClick,
    }
}
    
export default UseSourceListSelector

